export const PERMISSION_KEY = "livechat-platform";

export const initialState = {
  conversations: [],
  nextCursor: null,
  messages: [],
  nextBefore: null,
};

const activityOf = (conversation) => new Date(conversation.lastMessageAt ?? conversation.createdAt ?? 0).getTime();

const byActivity = (a, b) => activityOf(b) - activityOf(a);

const isNewer = (incoming, current) =>
  !current?.updatedAt || !incoming.updatedAt || new Date(incoming.updatedAt) >= new Date(current.updatedAt);

const LAST_SEQ = Number.MAX_SAFE_INTEGER;

// optimistic messages have no seq yet: they sit after the stored ones, in send order
const bySeq = (a, b) => (a.seq ?? LAST_SEQ) - (b.seq ?? LAST_SEQ) || new Date(a.created_at) - new Date(b.created_at);

// a delivered message replaces its optimistic copy (same client_id)
const settle = (messages) => {
  const delivered = new Set(messages.filter((message) => !message.local).map((message) => message.client_id));

  return messages.filter((message) => !message.local || !delivered.has(message.client_id)).sort(bySeq);
};

export const newClientId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();

  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0")).join("");

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const mergeById = (current, incoming, key) => {
  const map = new Map(current.map((item) => [item[key], item]));

  for (const item of incoming) {
    map.set(item[key], { ...map.get(item[key]), ...item });
  }

  return [...map.values()];
};

// Upserts by id, so repeated or out-of-order socket events never duplicate rows.
export const livechatReducer = (state, action) => {
  switch (action.type) {
    case "list/replace":
      return { ...state, conversations: [...action.items].sort(byActivity), nextCursor: action.nextCursor };

    case "list/append":
      return {
        ...state,
        conversations: mergeById(state.conversations, action.items, "conversationId").sort(byActivity),
        nextCursor: action.nextCursor,
      };

    case "conversation/upsert": {
      const { conversation } = action;
      const current = state.conversations.find((item) => item.conversationId === conversation.conversationId);

      if (current && !isNewer(conversation, current)) {
        return state;
      }

      return {
        ...state,
        conversations: mergeById(state.conversations, [conversation], "conversationId").sort(byActivity),
      };
    }

    case "conversation/remove":
      return {
        ...state,
        conversations: state.conversations.filter((item) => item.conversationId !== action.conversationId),
      };

    case "messages/replace":
      return { ...state, messages: settle([...action.messages]), nextBefore: action.nextBefore };

    case "messages/prepend":
      return {
        ...state,
        messages: settle(mergeById(state.messages, action.messages, "id")),
        nextBefore: action.nextBefore,
      };

    case "messages/merge":
      return { ...state, messages: settle(mergeById(state.messages, action.messages, "id")) };

    // only an optimistic copy still on screen: once delivered there is nothing to mark
    case "messages/localStatus":
      return state.messages.some((message) => message.id === action.id)
        ? {
            ...state,
            messages: state.messages.map((message) =>
              message.id === action.id ? { ...message, local: action.local } : message,
            ),
          }
        : state;

    // server messages and optimistic ones ({ local: "sending" | "failed" | "rejected" }) alike
    case "messages/upsert":
      return { ...state, messages: settle(mergeById(state.messages, [action.message], "id")) };

    default:
      return state;
  }
};

// Mirrors the server scope; the server still answers 403 for anything outside it.
export const accessOf = (permissions) => {
  const access = permissions?.[PERMISSION_KEY]?.access ?? [];

  if (access.includes("Access_All_Dept")) return "all";
  if (access.includes("Access_Own_Dept")) return "departments";

  return "own";
};

// Each role opens a conversation on its own status and unread columns, as the backend does
const ROLE_READ_FIELDS = {
  SALES: { status: "status", unread: "unreadCount" },
  CS: { status: "csStatus", unread: "unreadCountCs" },
  "SUPER ADMIN": { status: "superStatus", unread: "unreadCountSuper" },
  HEAD: { status: "headStatus", unread: "unreadCountHead" },
  AUDITOR: { status: "auditorStatus", unread: "unreadCountAuditor" },
  "SYSTEM ADMIN": { status: "sysAdminStatus", unread: "unreadCountSysAdmin" },
};
const ADMIN_READ_FIELDS = { status: "adminStatus", unread: "unreadCountAdmin" };

export const readFieldsOf = (roleName) => ROLE_READ_FIELDS[roleName] ?? ADMIN_READ_FIELDS;

// shows the viewer's own status and unread count as status / unreadCount
export const forRole = (conversation, fields) =>
  conversation && {
    ...conversation,
    status: conversation[fields.status] ?? conversation.status,
    unreadCount: conversation[fields.unread] ?? conversation.unreadCount,
  };

export const canSee = (conversation, me) => {
  if (me.access === "all") return true;
  if (me.access === "departments") return me.departmentIds.includes(conversation.departmentId);

  return conversation.userId === me.id;
};

// Newest unread visitor message of this conversation. Right after a switch the
// list still holds the previous conversation's messages: they never count.
export const unreadVisitorSeq = (messages, conversationId) =>
  messages.reduce(
    (max, message) =>
      message.conversation_id === conversationId &&
      message.sender?.type === "user" &&
      message.seq &&
      !message.read_at
        ? Math.max(max, message.seq)
        : max,
    0,
  );

// Only filters checkable on the row itself; tag, search and dates need the server.
export const matchesFilters = (conversation, filters) => {
  if (filters.tagId || filters.q || filters.dateFrom || filters.dateTo) return false;
  if (filters.status && conversation.status !== filters.status) return false;
  if (filters.departmentId && conversation.departmentId !== filters.departmentId) return false;
  if (filters.userId && conversation.userId !== filters.userId) return false;
  if (filters.channelId && conversation.channelId !== filters.channelId) return false;

  return true;
};

// Today, Yesterday or the date, as on the WhatsApp chat
export const dayLabel = (at, now = new Date()) => {
  const date = new Date(at);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  if (date.toDateString() === now.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

  return date.toLocaleDateString("en-GB");
};

// The list shows the time today, then Yesterday, then the date
export const listTimeLabel = (at, now = new Date()) => {
  if (!at) return "";

  const label = dayLabel(at, now);

  return label === "Today"
    ? new Date(at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })
    : label;
};

// The backend stores "[image]" when a media message has no caption
const MEDIA_PREVIEWS = {
  "[image]": { icon: "img-fill", label: "IMAGE" },
  "[video]": { icon: "video-fill", label: "VIDEO" },
  "[file]": { icon: "file-fill", label: "DOCUMENT" },
};

export const mediaPreviewOf = (preview) => MEDIA_PREVIEWS[preview] ?? null;

const dayOf = (at) => {
  const date = new Date(at);

  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

// Messages and notes in time order, with a chip per day and a separator whenever the ticket changes.
export const buildTimeline = (messages, notes = []) => {
  const entries = [
    ...messages.map((message) => ({ kind: "message", id: message.id, at: message.created_at, item: message })),
    ...notes.map((note) => ({ kind: "note", id: note.id, at: note.created_at, item: note })),
    // messages still being sent go last: their time comes from the browser clock
  ].sort((a, b) => Number(Boolean(a.item.local)) - Number(Boolean(b.item.local)) || new Date(a.at) - new Date(b.at));

  const timeline = [];
  let ticketId = null;
  let day = null;

  for (const entry of entries) {
    if (dayOf(entry.at) !== day) {
      day = dayOf(entry.at);
      timeline.push({ kind: "date", id: `date-${day}`, at: entry.at });
    }

    if (entry.item.ticket_id && entry.item.ticket_id !== ticketId) {
      ticketId = entry.item.ticket_id;
      timeline.push({ kind: "separator", id: `ticket-${ticketId}-${timeline.length}`, ticketId, at: entry.at });
    }

    timeline.push(entry);
  }

  return timeline;
};
